import type { SvelteComponent } from "svelte"
import { get } from "svelte/store"
import { _ } from "svelte-i18n"
import { getLogin } from "./api"
import { contentFloaterPath, location } from "./store"
import { customStatusIconsType1, customStatusIconsType2 } from "./config"
import { navigate } from "svelte-routing"
import { initialLocale, locales } from "./i18n"

export async function requireLogin(redirectTo?: string): Promise<Login> {
    try {
        return await getLogin()
    } catch (e) {
        if (!redirectTo) {
            const l = get(location)
            redirectTo = l.path + l.search
        }
        // split off protocol and domain and only let path
        redirectTo = redirectTo.replace(/^(?:\/\/|[^\/]+)*\//, "/")
        // direct redirect does not work
        window.setTimeout(() => {
            navigateTo("/de/login?redirectTo=" + encodeURIComponent(redirectTo as string), {
                replace: true,
            })
        }, 100)
    }
    return undefined
}

export function gotoWithLogin(node: Node, target?: string) {
    const clickHandler = (e: Event) => {
        e.preventDefault()
        if (!target) {
            // TODO more than <a> tag
            // @ts-ignore
            target = node.href
            // split off protocol and domain and only let path
            target = target.replace(/^(?:\/\/|[^\/]+)*\//, "/")
        }
        getLogin()
            .then(() => {
                navigateTo(target as string)
            })
            .catch(() => {
                navigateTo("/de/login?redirectTo=" + encodeURIComponent(target as string))
            })
    }

    node.addEventListener("click", clickHandler, true)
    return {
        destroy() {
            node.removeEventListener("click", clickHandler, true)
        },
    }
}

export function getQueryParam(param: string) {
    return new URLSearchParams(get(location).search).get(param)
}

function hasParentWithClass(element: HTMLElement, classname: string): any {
    if (typeof element.className == "string" && element.className.split(" ").indexOf(classname) >= 0) return true
    return element.parentNode && hasParentWithClass(element.parentNode as HTMLElement, classname)
}

export function clickOutside(node: Node, callback: Function) {
    const handleOutsideClick = ({ target }: MouseEvent) => {
        if (!node.contains(target as Node) && !hasParentWithClass(target as HTMLElement, "tooltip")) {
            callback(target)
        }
    }

    typeof window !== "undefined" && window.addEventListener("click", handleOutsideClick, true)

    return {
        destroy() {
            typeof window !== "undefined" && window.removeEventListener("click", handleOutsideClick, true)
        },
    }
}

function removeWhitespaces(inputString: string) {
    return inputString.replace(/\s+/g, "")
}

export function tooltip(node: Node, data: Tooltip | false) {
    let span: HTMLElement | undefined
    let when: string
    let doOpen: {}, doClose: () => void, clickOutside: (e: MouseEvent) => void

    function createTooltipContent(data: Tooltip) {
        if (!data) return

        if (typeof data.content === "string") {
            let clearedString = removeWhitespaces(data.content)
            if (!clearedString.length) return
        } else if (typeof data?.content?.props?.value === "string") {
            let clearedString = removeWhitespaces(data.content.props.value)
            if (!clearedString.length) return
        }

        const when = data.when || "hover"
        const span = document.createElement("div")

        const bubbleding = document.createElement("div")
        bubbleding.className = "bubbleding"
        span.appendChild(bubbleding)

        const content = document.createElement("div")
        content.className = "content"
        span.appendChild(content)

        const baseClass = "tooltip active " + (data.className || "")
        span.className = baseClass
        if (data.style) span.style.cssText = data.style
        let rendered = false
        const doOpen = (e: MouseEvent) => {
            if (typeof data.content == "string") {
                content.innerHTML = ""
                // @ts-ignore

                if (node.scrollWidth > node.clientWidth && !data.forceShow) {
                    if (data.overflowContent && data.overflowContent !== "undefined")
                        content.innerHTML =
                            "<div style='width: 100%;'>" +
                            `${get(_)("general.fullText")} ` +
                            data.overflowContent +
                            "</div>"
                } else {
                    if (data?.content == "invalid") {
                        doClose()
                        return
                    }
                }
                if (data.content !== "invalid" && content.innerHTML) content.innerHTML += "<br />" + data.content
                else if (data.content !== "invalid") content.innerHTML = data.content
            } else if (typeof data.content == "object" && !rendered) {
                if (
                    data?.content?.props?.value == "invalid" ||
                    (data?.overflowContent && data.overflowContent !== "undefined" && data?.optional)
                ) {
                    // @ts-ignore
                    if (node.scrollWidth > node.clientWidth) data.content.props.value = ""
                    else {
                        doClose()
                        return
                    }
                }

                rendered = true
                if (document.getElementsByClassName("tooltip").length == 0) {
                    const component = data.content.component as typeof SvelteComponent

                    new component({
                        target: content,
                        props: {
                            doClose,
                            ...data.content.props,
                        },
                    })
                    setTimeout(() => {
                        // @ts-ignore
                        if (node.scrollWidth > node.clientWidth) {
                            if (data.overflowContent && data.overflowContent !== "undefined") {
                                content.innerHTML +=
                                    "<br /><div style='width: 100%;'>" +
                                    `${get(_)("general.fullText")} ` +
                                    data.overflowContent +
                                    "</div>"
                            }
                        }
                    }, 100)
                }
            }
            let dialog = document.getElementsByClassName("dialog-open")
            let fullWith = window.innerWidth
            let fullHeight = window.innerHeight

            let dialogOffset = { x: 0, y: 0 }
            if (dialog.length > 0) {
                fullWith = dialog[0].clientWidth
                fullHeight = dialog[0].clientHeight

                let rect = dialog[0].getBoundingClientRect()
                dialogOffset.x = rect.left
                dialogOffset.y = rect.top
            }

            const mouseX = e.clientX
            const mouseY = e.clientY

            setTimeout(() => {
                let fixedCSSMarginLeft = 100
                let defaultOffset = 120

                let leftOffset = 0
                if (mouseX < defaultOffset) {
                    leftOffset = defaultOffset - mouseX
                }
                if (e.pageX > window.innerWidth - defaultOffset) {
                    leftOffset = defaultOffset * -1 + (fullWith - mouseX)
                }

                bubbleding.style.left = fixedCSSMarginLeft - leftOffset + "px"
                span.style.left = mouseX + leftOffset - dialogOffset.x + "px"

                if (data.className?.includes("bottom")) {
                    span.style.bottom = "auto"
                    span.style.top = mouseY + 20 - dialogOffset.y + "px"
                } else {
                    span.style.bottom = fullHeight - mouseY + 20 + dialogOffset.y + "px"
                }
            }, 0)

            if (!span.parentNode) {
                if (document.getElementsByClassName("tooltip").length == 0) {
                    if (dialog.length > 0) {
                        dialog[0].appendChild(span)
                    } else {
                        document.body.appendChild(span)
                    }
                }
            }
        }
        doClose = () => {
            span.remove()
        }

        clickOutside = ({ target }) => {
            if (!span.contains(target as Node)) {
                doClose()
            }
        }

        const closeBtn = document.createElement("span")
        closeBtn.innerHTML = "&#10005;"
        closeBtn.className = "tooltip-close"
        closeBtn.style.position = "absolute"
        closeBtn.style.top = "5px" // Positionieren Sie es oben
        closeBtn.style.right = "5px" // und rechts
        closeBtn.style.color = "#c4102d"
        closeBtn.style.cursor = "pointer" // Zeigen Sie den Cursor als Hand, um zu zeigen, dass es anklickbar ist
        closeBtn.addEventListener("click", doClose) // Fügt den Tooltip-Schließungs-Handler hinzu
        if (when == "click") closeBtn.className += " never-hide"
        span.appendChild(closeBtn)

        if (typeof window !== "undefined") {
            if (when == "click") {
                node.addEventListener("click", doOpen as EventListener, true)
                window.addEventListener("click", clickOutside, true)
            } else {
                node.addEventListener("mousemove", doOpen as EventListener, true)
                node.addEventListener("mouseout", doClose, true)
            }
            document.addEventListener("keydown", function (event) {
                if (event.key === "Escape") {
                    doClose()
                }
            })
        }

        return span
    }
    if (data) {
        span = createTooltipContent(data)
    }

    return {
        update(updatedData: any) {
            if (span && span.parentNode) {
                doClose() // This will handle the tooltip removal and its associated listeners
            }
            span = createTooltipContent(updatedData)
        },
        destroy() {
            Array.from(document.getElementsByClassName("tooltip")).forEach((el) => {
                el.remove()
            })

            if (typeof window !== "undefined") {
                if (when == "click") {
                    node.removeEventListener("click", doOpen as EventListener, true)
                    window.removeEventListener("click", clickOutside, true)
                } else {
                    node.removeEventListener("mouseover", doOpen as EventListener, true)
                    node.removeEventListener("mousemove", doOpen as EventListener, true)
                    node.removeEventListener("mouseout", doClose, true)
                }
                document.addEventListener("keydown", function (event) {
                    if (event.key === "Escape" && doClose) {
                        doClose()
                    }
                })
            }
        },
    }
}

export async function getRenderedComponent(
    // Svelte-Komponenten sind über ihre Props generisch typisiert; der
    // Konstruktor verlangt nur target und props. Deshalb hier offen halten.
    component: new (options: any) => SvelteComponent,
    props?: { [key: string]: any },
    tagName: string = "span"
): Promise<HTMLElement> {
    const node = document.createElement(tagName)

    return await new Promise((resolve) => {
        // need to execute out of svelte component context (strange side effects ????)
        window.setTimeout(() => {
            new component({
                target: node,
                props,
            })
            resolve(node)
        })
    })
}

function isObject(object: object | null) {
    return object != null && typeof object === "object"
}

export function objectIsEqual(object1: object, object2: object): boolean {
    if (!object1 && !object2) {
        return true
    }
    if (isObject(object1) && isObject(object2)) {
        const keys1 = Object.keys(object1)
        const keys2 = Object.keys(object2)

        if (keys1.length !== keys2.length) {
            return false
        }

        for (const key of keys1) {
            // @ts-ignore
            const val1 = object1[key]
            // @ts-ignore
            const val2 = object2[key]
            if (!objectIsEqual(val1, val2)) {
                return false
            }
        }

        return true
    } else {
        return object1 == object2
    }
}

export function contentFloater(node: Node, href?: string) {
    const clickHandler = function (e: Event) {
        e.preventDefault()
        // @ts-ignore
        contentFloaterPath.set(href || new URL(this.href).pathname)
    }
    node.addEventListener("click", clickHandler, true)
    return {
        destroy() {
            node.removeEventListener("click", clickHandler, true)
        },
    }
}

export function mongoRegexFilter(field: string, search: string): object {
    search = search.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") // $& meint den komplett erkannten String
    search = search.replace("\\*", ".*") // wildcard
    search = search.replace(/[uüúùû]e?/i, "[uüúùû]e?")
    search = search.replace(/[aäáàâ]e?/i, "[aäáàâ]e?")
    search = search.replace(/[oöóòô]e?/i, "[oöóòô]e?")
    search = search.replace(/[eéèêëę]/i, "[eéèêëę]")
    search = search.replace(/[cç]/i, "[cç]")
    search = search.replace(/[yÿ]/i, "[yÿ]")

    const f: { [key: string]: any } = {}
    f[field] = {
        $regex: search,
        $options: "i",
    }
    return f
}

export function mongoRegexNumberFilter(field: string, num: number | string): object | undefined {
    // does not work in $or with $text index, because all must have index and $expr is not with index
    num = parseInt(num.toString())
    if (!isNaN(num)) {
        return {
            $expr: {
                $regexMatch: {
                    input: { $toString: "$" + field },
                    regex: num.toString(),
                },
            },
        }
    }
}

export function textareaAutoHeight(node: Node) {
    // @ts-ignore
    const handleTextareaInput = (e) => {
        e.target.style.height = "auto"
        e.target.style.height = e.target.scrollHeight + "px"
    }

    if (typeof window !== "undefined") {
        node.addEventListener("input", handleTextareaInput)
        node.addEventListener("click", handleTextareaInput)
    }

    return {
        destroy() {
            node.removeEventListener("input", handleTextareaInput)
            node.removeEventListener("click", handleTextareaInput)
        },
    }
}

export const moneyFormat = (value: number, unit: string = "€", locale: Locale = "de-DE"): string => {
    let decimals = 2

    let formattedValue: string = ""

    if (typeof value != "number") {
        if (value) {
            if (decimals) {
                value = parseFloat(value)
            } else {
                value = parseInt(value)
            }
        } else {
            // @ts-ignore
            value = null
        }
    }

    if (typeof value == "number") {
        formattedValue = value.toLocaleString(locale, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        })

        if (unit) {
            formattedValue = `${formattedValue} ${unit}`
        }
    }

    return formattedValue
}

export const getOperationTextFilter = (_v: string, _addOr?: any[]) => {
    console.log(_addOr)
    const $or = []
    switch (true) {
        case !!_v.match(/^\d{1,8}$/): // nummer
            if (_v.length == 8) {
                // genaue Vorgangsnummer + Position (PositionImageField matcht auf Vorgangsnummer)
                $or.push({ number: parseInt(_v) })
                $or.push({
                    "positions.rikNumber": parseInt(_v),
                })
            } else if (_v.length == 7 || _v.length == 5) {
                // genaue Vorgangsnummer
                $or.push(
                    { number: parseInt(_v) },
                    {
                        "positions.rikNumber": {
                            $gte: parseInt(_v + "0"),
                            $lte: parseInt(_v + "9"),
                        },
                    }
                )
            } else if (_v.length == 6) {
                // evtl. Händlernummer
                $or.push(
                    { dealerNumber: parseInt(_v) },
                    {
                        number: {
                            $gte: parseInt(_v + "0"),
                            $lte: parseInt(_v + "9"),
                        },
                    },
                    {
                        "positions.rikNumber": {
                            $gte: parseInt(_v.padEnd(8, "0")),
                            $lte: parseInt(_v.padEnd(8, "9")),
                        },
                    }
                )
            } else {
                // Anfang einer Nummer
                $or.push(
                    {
                        dealerNumber: {
                            $gte: parseInt(_v.padEnd(6, "0")),
                            $lte: parseInt(_v.padEnd(6, "9")),
                        },
                    },
                    {
                        "positions.rikNumber": {
                            $gte: parseInt(_v.padEnd(8, "0")),
                            $lte: parseInt(_v.padEnd(8, "9")),
                        },
                    },
                    // genaue Nummer, egal wie lang
                    { number: parseInt(_v) }
                )
                // 2 bis 9 stellige Nummer
                for (let i = 2; i < 10; i++) {
                    $or.push({
                        number: {
                            $gte: parseInt(_v.padEnd(i, "0")),
                            $lte: parseInt(_v.padEnd(i, "9")),
                        },
                    })
                }
            }
            break
    }

    $or.push(
        {
            $text: { $search: _v },
        },
        mongoRegexFilter("inquiryNumber", _v),
        mongoRegexFilter("constructionProject", _v),
        mongoRegexFilter("constructionSite", _v),
        mongoRegexFilter("commission", _v),
        mongoRegexFilter("endCustomer.name", _v)
    )

    if (_addOr) {
        $or.push(..._addOr)
    }

    return { $or }
}

export const getFilterForAddressSearch = (search: string) => {
    const filter: any = {}

    filter.$or = [
        {
            $text: { $search: search },
        },
        {
            "company.name": { $regex: search, $options: "i" },
        },
        {
            "company.vatId": { $regex: search, $options: "i" },
        },
        {
            "person.firstname": { $regex: search, $options: "i" },
        },
        {
            "person.lastname": { $regex: search, $options: "i" },
        },
        {
            "location.street": { $regex: search, $options: "i" },
        },
        {
            "location.supplement": { $regex: search, $options: "i" },
        },
        {
            "location.postcode": { $regex: search, $options: "i" },
        },
        {
            "location.city": { $regex: search, $options: "i" },
        },
        {
            "contact.phone": { $regex: search, $options: "i" },
        },
        {
            "contact.fax": { $regex: search, $options: "i" },
        },
        {
            "contact.email": { $regex: search, $options: "i" },
        },
        {
            "contact.website": { $regex: search, $options: "i" },
        },

        {
            customerNumber: { $regex: search, $options: "i" },
        },
    ]

    return filter
}
export const getOpCustomStatus = (operation: Operation | OperationOut, type: "Internal" | "Dealer") =>
    operation[type == "Internal" ? "customInternalStatus" : "customDealerStatus"]
export const getOpCustomNote = (operation: Operation | OperationOut, type: "Internal" | "Dealer") =>
    operation[type == "Internal" ? "customInternalNote" : "customDealerNote"]
export const getCustomStatusIconObj = (operation: Operation | OperationOut) =>
    operation.type == 1 ? customStatusIconsType1 : customStatusIconsType2

export const navigateTo = (path: string, { replace = false } = {}, overwriteLocale = "") => {
    // validate path
    const lang = overwriteLocale ? overwriteLocale : initialLocale
    for (const locale of locales) {
        if (path.startsWith(`/${locale}`)) {
            path = path.replace(`/${locale}`, "")
            break
        }
    }
    if (!path.startsWith(`/${lang}`)) {
        path = `/${lang}${path.startsWith("/") ? "" : "/"}` + path
    }
    if (!path?.startsWith("/")) {
        throw new Error("invalid navigateTo: " + path)
    }
    // navigate
    navigate(path, { replace })
}
